import { createContext, useContext, useMemo } from "react";

import type { ConditionBuild, ConditionSlotId, EffectBuild } from "@skilitics/nautilus-core-designer-data";

import type { DesignerCondition, DesignerEffect } from "../types/index.ts";
import type { CreateCondition } from "./conditions/index.ts";
import type { CreateEffect, GetEffectBuildable } from "./effects/index.ts";
import type {
  ResolveAddableConditionBuildables,
  ResolveAddableEffectBuildables,
  ResolveConditionBuildables,
  ResolveEffectBuildables,
} from "./querying/index.ts";

export interface ResponseByIdContext {
  responseId: number;
  addEffect: (effect: EffectBuild) => void;
  getEffects: () => DesignerEffect[];
  updateEffect: (effectId: number, config: any) => void;
  reorderEffect: (effectId: number, newIndex: number) => void;
  removeEffect: (effectId: number) => void;
  resolveEffectBuildables: () => ReturnType<ResolveEffectBuildables>;
  resolveAddableEffectBuildables: () => ReturnType<ResolveAddableEffectBuildables>;
  createEffect: CreateEffect;
  getEffectBuildable: GetEffectBuildable;

  addRootCondition: (condition: ConditionBuild) => void;
  addCondition: (condition: ConditionBuild, slotId: ConditionSlotId) => void;
  createCondition: CreateCondition;
  getRootCondition: () => DesignerCondition | undefined;
  moveCondition: (conditionId: number, newSlotId: ConditionSlotId, newIndex?: number) => void;
  removeCondition: (conditionId: number) => void;
  reorderCondition: (conditionId: number, newIndex: number) => void;
  resolveConditionBuildables: () => ReturnType<ResolveConditionBuildables>;
  /** What may still be added, as against what already-built conditions resolve through. */
  resolveAddableConditionBuildables: () => ReturnType<ResolveAddableConditionBuildables>;
  updateCondition: (conditionId: number, config: any) => void;
}

export const ResponseByIdContext = createContext<ResponseByIdContext | undefined>(undefined);

ResponseByIdContext.displayName = "ResponseByIdContext";

export const useResponseDesigner = () => {
  const context = useContext(ResponseByIdContext);
  if (!context) {
    throw new Error(`Expected ResponseByIdProvider in component ancestry.`);
  }
  return context;
};

export const useIsRootCondition = (conditionId: number) => {
  const { getRootCondition } = useResponseDesigner();
  return useMemo(() => getRootCondition()?.id === conditionId, [conditionId, getRootCondition]);
};
